import { APP_CONSTANTS } from '@/config/constants';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { databaseService } from '@/services/databaseService';
import { globalState } from '@/state/globalState';
import { fetchFile } from '@/utils/file/fileHelpers';
import { sha512Hex } from './PendingOperationTerminalization';
import { reserveSyncExecution } from './SyncExecutionLease';
import { getSyncClientBuild } from './SyncCanaryFeatureService';
import { assertChecklistNotCancelled } from './ChecklistCancellationRuntime';
import { finalizeAcknowledgedChecklistMedia } from './ChecklistMediaAcknowledgement';
import { assertPendingSyncSessionCurrent, capturePendingSyncSession, createPendingSyncSupabaseClient,
  type PendingSyncSessionSnapshot } from './PendingSyncSession';

export const LEGACY_MEDIA_SCHEMA = 'checklist-legacy-media-recovery/v1';
const REASON = 'legacy_media_explicit_recovery';
const bytesOf = async (body: Blob | ArrayBuffer): Promise<Uint8Array> => {
  if (body instanceof ArrayBuffer) return new Uint8Array(body);
  if (typeof body.arrayBuffer === 'function') return new Uint8Array(await body.arrayBuffer());
  // O Blob nativo pode oferecer somente FileReader, sem arrayBuffer().
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Não foi possível ler os bytes da mídia.'));
    reader.onload = () => {
      try { resolve(naclUtil.decodeBase64(String(reader.result).split(',')[1])); } catch (error) { reject(error); }
    };
    reader.readAsDataURL(body);
  });
};
const hashBytes = async (body: Blob | ArrayBuffer) => Array.from(nacl.hash(await bytesOf(body)))
  .map(byte => byte.toString(16).padStart(2, '0')).join('');
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

async function inspect(pending: Record<string, any>, session: PendingSyncSessionSnapshot, receipt?: any) {
  if (pending.item_type !== 'CHECKLIST' || pending.user_id || !pending.campo_id ||
      (pending.empresa_id && pending.empresa_id !== session.empresaId)) throw new Error('Esta mídia não pertence à seleção sem autor.');
  const current = await databaseService.readItem<any>('pending_images', pending.id);
  if (!same(current, pending)) throw new Error('A mídia foi alterada. Selecione novamente.');
  const parent = await databaseService.readItem<any>('guincho_checklists', pending.item_id);
  const profile = globalState.user.profile.get();
  if (profile?.id !== session.userId || profile.empresa_id !== session.empresaId ||
      parent?.empresa_id !== session.empresaId || parent.retired_at ||
      (profile.user_type !== 'admin' && parent.usuario_id !== session.userId)) throw new Error('A recuperação exige o administrador ou responsável atual.');
  await assertChecklistNotCancelled(session.empresaId, pending.item_id);
  const expectedUrl = receipt ? createPendingSyncSupabaseClient(session).storage.from(receipt.bucket).getPublicUrl(receipt.path).data.publicUrl : null;
  const originalField = receipt?.value.campo_id;
  const values = (await databaseService.listItems<any>('guincho_valores_checklist', { checklist_id: pending.item_id }))
    .filter(row => row.empresa_id === session.empresaId && (row.snapshot_field_id === pending.campo_id || row.campo_id === pending.campo_id || (receipt && row.id === receipt.value.id)));
  if (values.length !== 1 || (values[0].valor !== pending.local_uri && values[0].valor !== expectedUrl)) throw new Error('A resposta local mudou ou possui vínculo ambíguo.');
  const value = values[0];
  const physicalField = value.campo_id ?? originalField;
  let snapshotFieldId: string | null = null;
  let authority: any;
  if (parent.snapshot_id) {
    const fields = (await databaseService.listItems<any>('guincho_checklist_snapshot_fields', { snapshot_id: parent.snapshot_id }))
      .filter(sf => sf.empresa_id === session.empresaId && sf.original_campo_id === physicalField);
    if (fields.length !== 1 || (value.snapshot_field_id && value.snapshot_field_id !== fields[0].id)) {
      throw new Error('Campo fora da versão atual ou com vínculo ambíguo.');
    }
    authority = fields[0];
    snapshotFieldId = authority.id;
  } else {
    if (value.snapshot_field_id) throw new Error('Snapshot local indisponível.');
    authority = await databaseService.readItem<any>('guincho_campos_template', physicalField);
    if (authority?.empresa_id !== session.empresaId || authority.template_id !== parent.template_id || authority.retired_at) throw new Error('Campo local indisponível.');
  }
  if (!['foto', 'assinatura', 'marcacao', 'marcacao_imagem'].includes(authority?.tipo)) throw new Error('O campo não é de mídia.');
  const operations = await databaseService.getPendingOperations();
  const fields = new Set([pending.campo_id, physicalField, value.campo_id, value.snapshot_field_id, snapshotFieldId].filter(Boolean));
  if (operations.some(op => {
    const itemId = op.itemId ?? (op as any).item_id;
    let data: any;
    try { data = typeof op.data === 'string' ? JSON.parse(op.data) : op.data; } catch { data = null; }
    if (op.operation === 'checklist_partial_update' && itemId === pending.item_id) {
      return !data || [...fields].some(field => Object.prototype.hasOwnProperty.call(data, field));
    }
    if (op.collection !== 'guincho_valores_checklist' || !['create', 'update', 'delete'].includes(op.operation)) return false;
    return itemId === value.id || data?.id === value.id ||
      (data?.checklist_id === pending.item_id && [data.campo_id, data.snapshot_field_id].some(field => fields.has(field)));
  })) throw new Error('Há uma edição pendente neste campo. Preserve a mídia e consulte o suporte.');
  const tombstones = await databaseService.listItems<any>('sync_tombstones', { empresa_id: session.empresaId });
  if (tombstones.some(t => [pending.item_id, value.id, value.campo_id, value.snapshot_field_id, parent.template_id].includes(t.entity_id))) {
    throw new Error('Há uma exclusão registrada para esta mídia ou checklist.');
  }
  await assertPendingSyncSessionCurrent(session);
  return { parent, value, snapshotFieldId, physicalField };
}

async function readReceipt(pending: Record<string, any>, session: PendingSyncSessionSnapshot) {
  const id = `legacy-media:${sha512Hex(`${session.empresaId}:${pending.id}:${session.userId}`).slice(0, 48)}`;
  const row = await databaseService.readItem<any>('sync_conflicts', id);
  if (!row) return { id, receipt: null };
  const receipt = JSON.parse(row.payload);
  if (row.payload_sha512 !== sha512Hex(row.payload) || receipt.schema !== LEGACY_MEDIA_SCHEMA ||
      receipt.recoveryActor !== session.userId || receipt.companyId !== session.empresaId || !same(receipt.pending, pending)) {
    throw new Error('O recibo anterior diverge da mídia selecionada.');
  }
  return { id, receipt };
}

export async function listLegacyChecklistMedia(): Promise<Record<string, any>[]> {
  const session = await capturePendingSyncSession();
  if (!session) throw new Error('Entre novamente para revisar as mídias.');
  const eligible = [];
  for (const pending of await databaseService.listItems<any>('pending_images')) {
    try { const saved = await readReceipt(pending, session); await inspect(pending, session, saved.receipt); eligible.push(pending); } catch { /* Itens sem prova permanecem intactos. */ }
  }
  return eligible;
}

/** Uma ação explícita nova; a autoria desconhecida da fila original nunca é reescrita. */
export async function recoverLegacyChecklistMedia(selected: Record<string, any>): Promise<void> {
  const release = reserveSyncExecution();
  if (!release) throw new Error('Aguarde a sincronização terminar.');
  try {
    const session = await capturePendingSyncSession();
    if (!session) throw new Error('Sessão não validada.');
    const saved = await readReceipt(selected, session);
    const { parent, value, snapshotFieldId, physicalField } = await inspect(selected, session, saved.receipt);
    const bytes = await fetchFile(selected.local_uri);
    const raw = await bytesOf(bytes);
    const kind = raw[0] === 0x89 && raw[1] === 0x50 && raw[2] === 0x4e && raw[3] === 0x47 && raw[4] === 13 && raw[5] === 10 && raw[6] === 26 && raw[7] === 10
      ? { extension: 'png', mime: 'image/png' }
      : raw[0] === 0xff && raw[1] === 0xd8 && raw[2] === 0xff ? { extension: 'jpg', mime: 'image/jpeg' }
      : String.fromCharCode(...raw.slice(0, 4)) === 'RIFF' && String.fromCharCode(...raw.slice(8, 12)) === 'WEBP'
        ? { extension: 'webp', mime: 'image/webp' } : null;
    const sourceKind = selected.local_uri.match(/(?:^|\/)checklist-(photos|signatures|marcacoes)\//)?.[1];
    if (!kind || !sourceKind) throw new Error('Arquivo sem tipo de imagem reconhecido.');
    const folder = sourceKind === 'signatures' ? APP_CONSTANTS.STORAGE.FOLDER.CHECKLIST_SIGNATURES
      : sourceKind === 'marcacoes' ? APP_CONSTANTS.STORAGE.FOLDER.CHECKLIST_MARCACOES : APP_CONSTANTS.STORAGE.FOLDER.CHECKLIST_PHOTOS;
    const bytesHash = await hashBytes(bytes);
    const id = saved.id;
    const client = createPendingSyncSupabaseClient(session);
    const parentResult = await client.from('guincho_checklists').select('id,empresa_id,snapshot_id,template_id,usuario_id,retired_at')
      .eq('id', selected.item_id).eq('empresa_id', session.empresaId).maybeSingle();
    await assertPendingSyncSessionCurrent(session);
    if (parentResult.error) throw parentResult.error;
    if (!parentResult.data) throw new Error('Sincronize primeiro este checklist na conta que o criou. A mídia foi preservada.');
    if (parentResult.data.id !== parent.id || parentResult.data.empresa_id !== session.empresaId ||
        parentResult.data.snapshot_id !== parent.snapshot_id || parentResult.data.template_id !== parent.template_id || parentResult.data.retired_at) {
      throw new Error('O checklist remoto diverge da versão local.');
    }
    let receipt: any = saved.receipt;
    if (receipt) {
      if (receipt.value.id !== value.id || receipt.value.checklist_id !== value.checklist_id ||
          receipt.value.empresa_id !== value.empresa_id || receipt.snapshotFieldId !== snapshotFieldId ||
          ['id','empresa_id','template_id','snapshot_id','usuario_id','retired_at'].some(key =>
            (receipt.parent[key] ?? null) !== (parent[key] ?? null)) || receipt.bytesSha512 !== bytesHash) {
        throw new Error('O recibo anterior diverge da mídia atual; nenhuma escrita autorizada.');
      }
    } else {
      const { data: remote, error } = await client.from('guincho_valores_checklist').select('*')
        .eq('empresa_id', session.empresaId).eq('checklist_id', selected.item_id).eq('id', value.id).maybeSingle();
      if (error) throw error;
      const path = `${folder}/${session.empresaId}/${selected.item_id}/recovery-${bytesHash}.${kind.extension}`;
      receipt = { schema: LEGACY_MEDIA_SCHEMA, originalAuthor: 'unknown', recoveryActor: session.userId,
        companyId: session.empresaId, pending: selected, value, parent, snapshotFieldId, bytesSha512: bytesHash,
        path, bucket: APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST, contentType: kind.mime,
        referenceMode: !snapshotFieldId || (remote && !remote.snapshot_field_id && remote.campo_id === physicalField) ? 'physical' : 'snapshot', expectedUpdatedAt: remote?.updated_at ?? null,
        idempotencyKey: `legacy-media:${sha512Hex(id + bytesHash).slice(0, 80)}` };
      const payload = JSON.stringify(receipt);
      await assertPendingSyncSessionCurrent(session);
      await databaseService.createItem('sync_conflicts', { id, empresa_id: session.empresaId, user_id: session.userId,
        pending_operation_id: null, collection: 'pending_images', item_id: selected.id, reason: REASON,
        payload, payload_sha512: sha512Hex(payload), status: 'media_recovery_prepared',
        created_at: Date.now(), updated_at: Date.now(), resolved_at: null }, true);
    }
    await inspect(selected, session, receipt);
    if (await hashBytes(await fetchFile(selected.local_uri)) !== receipt.bytesSha512) throw new Error('Os bytes locais mudaram.');
    await assertPendingSyncSessionCurrent(session);
    const storage = client.storage.from(receipt.bucket);
    const { error: uploadError } = await storage.upload(receipt.path, bytes, { upsert: false, contentType: receipt.contentType });
    if (uploadError && !/already exists|duplicate|resource.*exists/i.test(uploadError.message)) throw uploadError;
    await assertPendingSyncSessionCurrent(session);
    const { data: downloaded, error: downloadError } = await storage.download(receipt.path);
    if (downloadError || !downloaded || await hashBytes(downloaded) !== receipt.bytesSha512) throw new Error('O servidor não confirmou os mesmos bytes.');
    const publicUrl = storage.getPublicUrl(receipt.path).data.publicUrl;
    await inspect(selected, session, receipt);
    const { data, error } = await client.rpc('recover_legacy_checklist_media_v1', {
      p_checklist_id: selected.item_id, p_value: { id: value.id, campo_id: physicalField,
        snapshot_field_id: receipt.snapshotFieldId, valor: publicUrl,
        recovery_reference_mode: receipt.referenceMode, expected_updated_at: receipt.expectedUpdatedAt, idempotency_key: receipt.idempotencyKey },
      p_client_build: getSyncClientBuild(),
    });
    await assertPendingSyncSessionCurrent(session);
    if (error || !Array.isArray(data) || data.length !== 1 || !['applied', 'historical'].includes(data[0].result) || data[0].value_id !== value.id) {
      throw error ?? new Error('A resposta remota não confirmou esta recuperação.');
    }
    await inspect(selected, session, receipt);
    if (!await finalizeAcknowledgedChecklistMedia({ pending: selected, value, publicUrl, snapshot: session,
      recoveryReceiptId: id, storageLocation: { bucket: receipt.bucket, path: receipt.path } })) {
      throw new Error('A confirmação final ainda está pendente. A mídia local foi preservada.');
    }
  } finally { release(); }
}
